/**
 * Structured logger service using Pino with OpenTelemetry integration.
 *
 * This module provides:
 * - JSON-structured logs sent to backend
 * - Pretty console output for development
 * - Category-based logging (AUTH, API, UI, NAVIGATION, PERFORMANCE, ERROR)
 * - Automatic correlation ID and trace ID injection
 * - Helper methods for common logging patterns
 * - Performance monitoring integration
 *
 * Usage:
 *   import { logger, logAPI, logUserAction, logError } from '@/services/logger';
 *
 *   // General logging
 *   logger.info({ category: 'UI', action: 'button_click' }, 'User clicked button');
 *
 *   // Category-specific helpers
 *   logAPI.request('GET', '/api/users');
 *   logAPI.response('GET', '/api/users', 200, 125);
 *   logUserAction('login', { userId: 'user123' });
 *   logError.exception('Failed to load data', error);
 */

import pino from 'pino';
import { v4 as uuidv4 } from 'uuid';

// Import shared types (re-export for backwards compatibility)
export { LogCategory, type LogContext, type LogLevel, type LogEntry } from '@knitknot/shared/types/logging';
import { LogCategory, type LogContext } from '@knitknot/shared/types/logging';
import { API_URL } from '@knitknot/shared/lib/appUrls';

// ============================================================================
// CONFIGURATION
// ============================================================================

const IS_DEVELOPMENT = import.meta.env.DEV;
// Prod stays at warn — it was info (noisier than dev!), POSTing log batches to
// the backend during boot that scaled with API traffic (perf plan §4.10).
const LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL || 'warn';
// `/api` under the dev-server proxy; otherwise the env-resolved API origin
// (appUrls) — never a port literal, which is wrong in every worktree.
const BACKEND_URL = import.meta.env.VITE_API_BASE_URL || API_URL;
// Backend log shipping is opt-IN now (was opt-out): only warn+ ships, and only
// when VITE_ENABLE_BACKEND_LOGGING=true is set explicitly.
const ENABLE_BACKEND_LOGGING = import.meta.env.VITE_ENABLE_BACKEND_LOGGING === 'true';

interface BackendLogEntry {
  level: string;
  timestamp: string;
  message: string;
  category?: string;
  correlationId?: string;
  traceId?: string;
  spanId?: string;
  url: string;
  userAgent: string;
  context?: Record<string, any>;
}

// ============================================================================
// BACKEND TRANSPORT
// ============================================================================

/**
 * Custom transport that sends logs to the backend API.
 */
class BackendTransport {
  private queue: BackendLogEntry[] = [];
  private batchSize = 10;
  private flushInterval = 5000; // 5 seconds
  private timer: NodeJS.Timeout | null = null;

  constructor() {
    if (ENABLE_BACKEND_LOGGING) {
      this.startFlushTimer();
    }
  }

  /**
   * Add a log entry to the queue.
   */
  public log(entry: BackendLogEntry): void {
    if (!ENABLE_BACKEND_LOGGING) return;

    this.queue.push(entry);

    // Flush if batch size reached
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Start periodic flush timer.
   */
  private startFlushTimer(): void {
    this.timer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.flushInterval);
  }

  /**
   * Flush queued logs to backend.
   */
  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = [...this.queue];
    this.queue = [];

    try {
      // Send logs to backend (fire and forget, don't block on errors)
      await fetch(`${BACKEND_URL}/api/v1/dev/browser-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: batch }),
      }).catch(() => {
        // Silently fail - don't break app if logging fails
        // In development, we can still see logs in console
      });
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Stop flush timer (cleanup).
   */
  public stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.flush(); // Final flush
  }
}

const backendTransport = new BackendTransport();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    backendTransport.stop();
  });
}

// ============================================================================
// PINO LOGGER SETUP
// ============================================================================

/**
 * Create Pino logger instance with custom configuration.
 */
const pinoLogger = pino({
  level: LOG_LEVEL,
  browser: {
    asObject: true,
    serialize: true,
    write: {
      trace: (o: any) => sendLog('trace', o),
      debug: (o: any) => sendLog('debug', o),
      info: (o: any) => sendLog('info', o),
      warn: (o: any) => sendLog('warn', o),
      error: (o: any) => sendLog('error', o),
      fatal: (o: any) => sendLog('fatal', o),
    },
  },
  base: {
    source: 'browser',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  },
});

/**
 * Send log to backend and console.
 */
function sendLog(level: string, logObj: any): void {
  // Pretty print to console in development
  if (IS_DEVELOPMENT) {
    const style = getConsoleStyle(level);
    const category = logObj.category ? `[${logObj.category}]` : '';
    const message = logObj.msg || '';

    console.log(
      `%c${level.toUpperCase()} ${category}`,
      style,
      message,
      logObj
    );
  }

  // Send to backend
  if (ENABLE_BACKEND_LOGGING) {
    const entry: BackendLogEntry = {
      level: level.toUpperCase(),
      timestamp: new Date().toISOString(),
      message: logObj.msg || '',
      category: logObj.category,
      correlationId: logObj.correlationId,
      traceId: logObj.traceId,
      spanId: logObj.spanId,
      url: logObj.url || window.location.href,
      userAgent: logObj.userAgent || navigator.userAgent,
      context: logObj,
    };

    backendTransport.log(entry);
  }
}

/**
 * Get console styling based on log level.
 */
function getConsoleStyle(level: string): string {
  const styles: Record<string, string> = {
    trace: 'color: gray; font-weight: normal',
    debug: 'color: cyan; font-weight: normal',
    info: 'color: blue; font-weight: normal',
    warn: 'color: orange; font-weight: bold',
    error: 'color: red; font-weight: bold',
    fatal: 'color: red; font-weight: bold; background: yellow',
  };
  return styles[level] || 'color: black';
}

// ============================================================================
// CONTEXT ENRICHMENT
// ============================================================================

/**
 * Generate correlation ID for the session.
 */
let sessionCorrelationId: string | null = null;

export function getCorrelationId(): string {
  if (!sessionCorrelationId) {
    sessionCorrelationId = `browser-${uuidv4()}`;
  }
  return sessionCorrelationId;
}

/**
 * Enrich log context with automatic fields.
 */
function enrichContext(context: LogContext = {}): LogContext {
  return {
    correlationId: getCorrelationId(),
    url: window.location.href,
    route: window.location.pathname,
    ...context,
  };
}

// ============================================================================
// MAIN LOGGER API
// ============================================================================

/**
 * Main logger instance with enriched context.
 */
export const logger = {
  /**
   * Log debug message (development only).
   */
  debug(context: LogContext, message: string): void {
    pinoLogger.debug(enrichContext(context), message);
  },

  /**
   * Log info message.
   */
  info(context: LogContext, message: string): void {
    pinoLogger.info(enrichContext(context), message);
  },

  /**
   * Log warning message.
   */
  warn(context: LogContext, message: string): void {
    pinoLogger.warn(enrichContext(context), message);
  },

  /**
   * Log error message.
   */
  error(context: LogContext, message: string): void {
    pinoLogger.error(enrichContext(context), message);
  },

  /**
   * Log fatal error message.
   */
  fatal(context: LogContext, message: string): void {
    pinoLogger.fatal(enrichContext(context), message);
  },
};

// ============================================================================
// API LOGGING HELPERS
// ============================================================================

export const logAPI = {
  /**
   * Log API request.
   */
  request(method: string, url: string, context: LogContext = {}): void {
    logger.info(
      {
        ...context,
        category: LogCategory.API,
        method,
        url,
        action: 'request',
      },
      `API Request: ${method} ${url}`
    );
  },

  /**
   * Log API response.
   *
   * `expected` demotes a >=400 status back to info: the caller declared it a
   * legitimate answer rather than a failure (see `expectedStatuses` in
   * apiClient). Without this, an existence probe whose 404 means "no" still
   * printed at error level from HERE even once the error-path log was
   * suppressed — the status check runs before the `response.ok` branch, so
   * both sites have to agree on what counts as a failure.
   */
  response(
    method: string,
    url: string,
    status: number,
    durationMs: number,
    context: LogContext = {},
    expected = false
  ): void {
    const logFunc = status >= 400 && !expected ? logger.error : logger.info;

    logFunc(
      {
        ...context,
        category: LogCategory.API,
        method,
        url,
        status,
        durationMs,
        action: 'response',
      },
      `API Response: ${method} ${url} ${status} (${durationMs}ms)`
    );
  },

  /**
   * Log API error.
   */
  error(method: string, url: string, error: Error, context: LogContext = {}): void {
    logger.error(
      {
        ...context,
        category: LogCategory.API,
        method,
        url,
        error: error.message,
        errorStack: error.stack,
        action: 'error',
      },
      `API Error: ${method} ${url} - ${error.message}`
    );
  },
};

// ============================================================================
// USER ACTION LOGGING
// ============================================================================

/**
 * Log user actions (clicks, form submissions, etc.).
 */
export function logUserAction(
  action: string,
  context: LogContext = {}
): void {
  logger.info(
    {
      ...context,
      category: LogCategory.UI,
      action,
    },
    `User action: ${action}`
  );
}

// ============================================================================
// NAVIGATION LOGGING
// ============================================================================

/**
 * Log navigation events.
 */
export function logNavigation(
  from: string,
  to: string,
  context: LogContext = {}
): void {
  logger.info(
    {
      ...context,
      category: LogCategory.NAVIGATION,
      from,
      to,
    },
    `Navigation: ${from} → ${to}`
  );
}

// ============================================================================
// PERFORMANCE LOGGING
// ============================================================================

export const logPerformance = {
  /**
   * Log component render time.
   */
  render(component: string, durationMs: number, context: LogContext = {}): void {
    logger.debug(
      {
        ...context,
        category: LogCategory.PERFORMANCE,
        component,
        durationMs,
        metric: 'render',
      },
      `Component render: ${component} (${durationMs}ms)`
    );
  },

  /**
   * Log data fetch time.
   */
  fetch(resource: string, durationMs: number, context: LogContext = {}): void {
    const logFunc = durationMs > 1000 ? logger.warn : logger.debug;

    logFunc(
      {
        ...context,
        category: LogCategory.PERFORMANCE,
        resource,
        durationMs,
        metric: 'fetch',
      },
      `Data fetch: ${resource} (${durationMs}ms)`
    );
  },
};

// ============================================================================
// ERROR LOGGING
// ============================================================================

export const logError = {
  /**
   * Log exception with full context.
   */
  exception(message: string, error: Error, context: LogContext = {}): void {
    logger.error(
      {
        ...context,
        category: LogCategory.ERROR,
        error: error.message,
        errorStack: error.stack,
        errorName: error.name,
      },
      message
    );
  },

  /**
   * Log validation error.
   */
  validation(field: string, value: any, reason: string, context: LogContext = {}): void {
    logger.warn(
      {
        ...context,
        category: LogCategory.ERROR,
        field,
        value,
        reason,
        type: 'validation',
      },
      `Validation error: ${field} - ${reason}`
    );
  },
};

// ============================================================================
// GLOBAL ERROR HANDLERS
// ============================================================================

/**
 * Install global error handlers.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    logError.exception('Unhandled error', event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError.exception(
      'Unhandled promise rejection',
      event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    );
  });
}
